import { defineStore } from 'pinia'
import {
  DRCData,
  DroneControlData,
  DroneControlFences,
  InteractiveMessageCode,
  JourneyStep,
  LiveData,
  ServerCallCode,
  StartSkyshotData,
  StartTakePhotoData,
} from '@/models/aerial'
import { StorageKey } from '@/models/enums'
import { UserOrdersResponseV3 } from '@/models/order'

const STORE_KEY = 'journey'

export const useJourneyStore = defineStore({
  id: STORE_KEY,
  state: () => ({
    runningJourney: {
      ticketId: '',
      hotspotId: '',
    } as UserOrdersResponseV3,
    currentJourneyStep: JourneyStep.SKYSHOT_JOURNEY_TUTORIAL as JourneyStep,
    currentJourneyMessageCode:
      InteractiveMessageCode.SKYSHOT_JOURNEY_TUTORIAL_COMPLETED_REPLY as InteractiveMessageCode,
    filterList: [
      InteractiveMessageCode.SKYSHOT_JOURNEY_HEARTBEAT_REPLY,
      InteractiveMessageCode.SKYSHOT_JOURNEY_DRC_ACTION_REPLY,
      InteractiveMessageCode.SKYSHOT_JOURNEY_SYNC_CONNECTION_REPLY,
      InteractiveMessageCode.SKYSHOT_JOURNEY_READY,
      InteractiveMessageCode.SKYSHOT_JOURNEY_DRC_ACTION_RESULT,
    ] as InteractiveMessageCode[],
    droneControlData: { seq: 0, x: 0, y: 0, h: 0, w: 0 } as DroneControlData,
    droneControlFences: { x: 0, y: 0, h: 0 } as DroneControlFences,
    resetJourneyStoreTimer: null as any,
    droneSettings: {
      forwardBackward: 6, // 前进后退速度 [1, 17]
      leftRight: 6, // 左飞右飞速度 [1, 17]
      upDown: 3, // 上升下降速度 [1, 4]
      yawRotation: 12, // 左转右转角速度 [1, 90]
      gimbal: 10, // 云台控制倍数 [1, 10]
    },
  }),
  getters: {
    journeyTutorial: (state) => {
      return state.currentJourneyStep === JourneyStep.SKYSHOT_JOURNEY_TUTORIAL
    },
    journeyIsInit: (state) => {
      return state.currentJourneyStep === JourneyStep.SKYSHOT_JOURNEY_TUTORIAL_COMPLETED_REPLY
    },
    journeyIsPlay: (state) => {
      return (
        state.currentJourneyStep === JourneyStep.SKYSHOT_JOURNEY_START_LIVE ||
        state.currentJourneyStep === JourneyStep.SKYSHOT_JOURNEY_START_FLIGHT ||
        state.currentJourneyStep === JourneyStep.SKYSHOT_JOURNEY_TAKEOFF ||
        state.currentJourneyStep === JourneyStep.SKYSHOT_JOURNEY_PHOTO_TIME_START ||
        state.currentJourneyStep === JourneyStep.SKYSHOT_JOURNEY_PHOTO_TIME_END ||
        state.currentJourneyStep === JourneyStep.SKYSHOT_JOURNEY_DRC_ON ||
        state.currentJourneyStep === JourneyStep.SKYSHOT_JOURNEY_DRC_OFF ||
        state.currentJourneyStep === JourneyStep.SKYSHOT_JOURNEY_SKYSHOT_START ||
        state.currentJourneyStep === JourneyStep.SKYSHOT_JOURNEY_SKYSHOT_END ||
        state.currentJourneyStep === JourneyStep.SKYSHOT_JOURNEY_GO_HOME
      )
    },
    // 旅程状态：航拍中
    journeyIsPlaying: (state) => {
      const steps = [
        JourneyStep.SKYSHOT_JOURNEY_START_FLIGHT,
        JourneyStep.SKYSHOT_JOURNEY_TAKEOFF,
        JourneyStep.SKYSHOT_JOURNEY_PHOTO_TIME_START,
        JourneyStep.SKYSHOT_JOURNEY_PHOTO_TIME_END,
        JourneyStep.SKYSHOT_JOURNEY_DRC_ON,
        JourneyStep.SKYSHOT_JOURNEY_DRC_OFF,
        JourneyStep.SKYSHOT_JOURNEY_SKYSHOT_START,
        JourneyStep.SKYSHOT_JOURNEY_SKYSHOT_END,
      ]
      return steps.includes(state.currentJourneyStep)
    },
    journeyIsEnd: (state) => {
      return (
        state.currentJourneyStep === JourneyStep.SKYSHOT_JOURNEY_LANDING ||
        state.currentJourneyStep === JourneyStep.SKYSHOT_JOURNEY_END
      )
    },
    joystickDroneLeftStickLightBg: ({ droneControlFences, droneControlData }) => {
      // if ((droneControlFences.h === 1 && droneControlData.h! > 0) || (droneControlFences.h === -1 && droneControlData.h! < 0)) {
      //     return 'warning'
      // }
      return 'normal'
    },
    joystickDroneRightStickLightBg: ({ droneControlFences, droneControlData }) => {
      if (
        (droneControlFences.x === 1 && droneControlData.x! > 0) ||
        (droneControlFences.x === -1 && droneControlData.x! < 0)
      ) {
        return 'warning'
      }
      if (
        (droneControlFences.y === 1 && droneControlData.y! > 0) ||
        (droneControlFences.y === -1 && droneControlData.y! < 0)
      ) {
        return 'warning'
      }
      return 'normal'
    },
    // droneControlFencesTopLine: ({ droneControlFences, droneControlData }) => {
    //     if ((droneControlFences.h === 1 && droneControlData.h! > 0) || (droneControlFences.x === 1 && droneControlData.x! > 0)) {
    //         return true
    //     }
    //     return false
    // },
    droneControlFencesTopLine: ({ droneControlFences, droneControlData }) => {
      if (droneControlFences.h === 1 || (droneControlFences.x === 1 && droneControlData.x! > 0)) {
        return true
      }
      return false
    },
    droneControlFencesRightLine: ({ droneControlFences, droneControlData }) => {
      if (droneControlFences.y === 1 && droneControlData.y! > 0) {
        return true
      }
      return false
    },
    // droneControlFencesBottomLine: ({ droneControlFences, droneControlData }) => {
    //     if ((droneControlFences.h === -1 && droneControlData.h! < 0) || (droneControlFences.x === -1 && droneControlData.x! < 0)) {
    //         return true
    //     }
    //     return false
    // },
    droneControlFencesBottomLine: ({ droneControlFences, droneControlData }) => {
      if (droneControlFences.h === -1 || (droneControlFences.x === -1 && droneControlData.x! < 0)) {
        return true
      }
      return false
    },
    droneControlFencesLeftLine: ({ droneControlFences, droneControlData }) => {
      if (droneControlFences.y === -1 && droneControlData.y! < 0) {
        return true
      }
      return false
    },
  },
  actions: {
    setCurrentJourneyMessageCode(code: InteractiveMessageCode, data?: any) {
      if (this.filterList.includes(code)) return
      this.currentJourneyMessageCode = code
      // if (this.isJourneyCode(code)) {
      //     this.setCurrentJourneyStep(code, data)
      // }
    },
    /**
     * Set the current journey step based on the given code and data.
     *
     * This function is used to set the current journey step based on the given code and data.
     * The code is used to determine which step to set, and the data is used to determine the
     * specific state of the step.
     *
     * @param code The code to determine which step to set.
     * @param data The data to determine the specific state of the step.
     */
    // setCurrentJourneyStep(code: JourneyCode, data?: any): void {
    //     try {
    //         this.currentJourneyStep = code as unknown as JourneyStep
    //     } catch (error) {
    //         console.error('unknown websocket code', error)
    //     }
    // },
    setCurrentJourneyStepBySynchronized(step: JourneyStep) {
      // 如果当前步骤是以下任一值
      const currentIsInProgress = [
        JourneyStep.SKYSHOT_JOURNEY_PHOTO_TIME_START,
        JourneyStep.SKYSHOT_JOURNEY_PHOTO_TIME_END,
        JourneyStep.SKYSHOT_JOURNEY_DRC_ON,
        JourneyStep.SKYSHOT_JOURNEY_DRC_OFF,
        JourneyStep.SKYSHOT_JOURNEY_SKYSHOT_START,
        JourneyStep.SKYSHOT_JOURNEY_SKYSHOT_END,
        JourneyStep.SKYSHOT_JOURNEY_GO_HOME,
        JourneyStep.SKYSHOT_JOURNEY_LANDING,
        JourneyStep.SKYSHOT_JOURNEY_END,
      ].includes(this.currentJourneyStep)

      // 如果传入的步骤是以下任一值
      const newStepIsEarly = [
        JourneyStep.SKYSHOT_JOURNEY_START_LIVE,
        JourneyStep.SKYSHOT_JOURNEY_START_FLIGHT,
        JourneyStep.SKYSHOT_JOURNEY_TAKEOFF,
      ].includes(step)

      // 如果当前步骤是进行中或结束状态，且传入的是早期步骤，则保持当前步骤不变
      if (currentIsInProgress && newStepIsEarly) {
        return
      }

      // 否则更新步骤
      this.currentJourneyStep = step
    },
    clearDroneControlFencesH() {
      if (this.droneControlFences.h === 1 || this.droneControlFences.h === -1) {
        this.droneControlFences.h = 0
      }
    },
    // isJourneyCode(code: unknown): code is JourneyCode {
    //     return Object.values(JourneyCode).includes(code as JourneyCode)
    // },
    clearResetJourneyStoreTimer() {
      if (this.resetJourneyStoreTimer) {
        clearInterval(this.resetJourneyStoreTimer)
        this.resetJourneyStoreTimer = null
      }
    },
    viewMemory(ticket_id: string) {
      const gStore = useGlobalStore()
      if (gStore.inMP) {
        window.uni.navigateTo({
          url: `/pages-memory/detail/index?journeyId=${ticket_id}&ticketId=${ticket_id}`,
        })
        return
      }
      router.push({
        name: PageName.MEMORY_DETAIL,
        params: { journey_id: ticket_id, ticket_id },
      })
    },
  },
})
